/**
 * AIReviewTab — generates a grounded manager review draft via the proxy.
 *
 * Helper components live in AIReviewTabHelpers.tsx.
 * Prompt construction lives in buildReviewPrompt.ts.
 */
import React, { useState } from 'react';
import { C, FINN_VALUES } from '../../data/managerReviewData';
import { authenticatedFetch } from '../../../../shared/utils/authenticatedFetch';
import type { Employee, BehaviorData, Deliverable, FeedbackItem, LeadershipData } from '../../types/managerReviewV2';
import { cultureAmpLeadershipAnswer, leadershipConfirmed } from './leadershipValues';
import {
  QualityDisplay, CARatingField, CATextField, CAPromotionField, CAChoiceField,
} from './AIReviewTabHelpers';
import type { PromotionChoice } from '../../types/managerReviewV2';
import { buildReviewPrompt } from './buildReviewPrompt';
import { FIELD_MARKERS, parseOutput } from './reviewOutputTypes';
export type { ParsedOutput } from './reviewOutputTypes';
import type { ParsedOutput } from './reviewOutputTypes';

/** Generated text fields: Q3 Deliverables, Q4 Leadership Values, Q11 Behaviors, Q13 Calibration summary. */
type Field = 'delivery' | 'leadership' | 'behaviors' | 'calibration';
const FIELD_KEY = { delivery: 'deliverables', leadership: 'leadership', behaviors: 'behaviors', calibration: 'calibration' } as const;
const FIELD_LABEL = { delivery: 'Field 3', leadership: 'Field 4', behaviors: 'Field 11', calibration: 'Field 13' } as const;

const PROXY_URL =
  import.meta.env.VITE_PROXY_URL ||
  'https://proxy-prod-520260363689.europe-west3.run.app';

interface AIReviewTabProps {
  employee: Employee;
  level: string;
  behaviorsData: Record<string, BehaviorData>;
  behaviorsRating: number | null;
  behaviorsRatingNote: string;
  deliverables: Deliverable[];
  deliveryRating: number | null;
  leadership: LeadershipData;
  overallRating: number | null;
  overallRatingNote: string;
  promotion: PromotionChoice | null;
  promotionNote: string;
  biasNote: string;
  selfReview: string;
  perfFeedbackRaw?: string;
  anytimeFeedbackRaw?: string;
  feedbackItems: FeedbackItem[];
  generatedReview: ParsedOutput | null;
  setGeneratedReview: React.Dispatch<React.SetStateAction<ParsedOutput | null>>;
  onNavigate?: (tab: number, anchor?: string) => void;
  onOpenFeedback?: () => void;
}

export default function AIReviewTab({
  employee, level, behaviorsData, behaviorsRating, behaviorsRatingNote,
  deliverables, deliveryRating, leadership, overallRating, overallRatingNote,
  promotion, promotionNote, biasNote, selfReview, perfFeedbackRaw,
  anytimeFeedbackRaw, feedbackItems,
  generatedReview, setGeneratedReview, onNavigate, onOpenFeedback,
}: AIReviewTabProps) {
  const [loading, setLoading] = useState(false);
  const [loadingField, setLoadingField] = useState<Field | null>(null);
  const [refiningField, setRefiningField] = useState<Field | null>(null);
  const [error, setError] = useState('');
  const [editedFields, setEditedFields] = useState<Set<Field>>(new Set());
  const [confirmRegenerate, setConfirmRegenerate] = useState(false);

  const markEdited = (field: Field) =>
    setEditedFields(prev => new Set([...prev, field]));

  const parsed = generatedReview;
  const setParsed = setGeneratedReview;

  const promptData = {
    employee, level, behaviorsData, behaviorsRating, behaviorsRatingNote,
    deliverables, deliveryRating, leadership, overallRating, overallRatingNote,
    promotion, promotionNote, biasNote, selfReview, perfFeedbackRaw,
    anytimeFeedbackRaw, feedbackItems,
  };
  const hasLeadership = leadershipConfirmed(employee, leadership);
  const leadershipAnswer = cultureAmpLeadershipAnswer(employee, leadership);

  const callModel = async (prompt: string): Promise<string> => {
    const res = await authenticatedFetch(`${PROXY_URL}/api/people/manager-review-v2/generate`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt }),
    });
    const data = await res.json() as { text?: string; error?: string };
    if (!res.ok) throw new Error(data.error || `Request failed with status ${res.status}`);
    return data.text || '';
  };

  const afterMarker = (text: string, marker: string) => {
    const i = text.indexOf(marker);
    return (i === -1 ? text : text.slice(i + marker.length)).trim();
  };

  // One call for the quality check and one per field, in parallel: no field can be cut off by
  // another's length, and each answer stays focused on its own notes.
  const generate = async () => {
    setConfirmRegenerate(false);
    setEditedFields(new Set());
    setLoading(true);
    setParsed(null);
    setError('');
    try {
      const fields: Field[] = hasLeadership ? ['delivery', 'leadership', 'behaviors', 'calibration'] : ['delivery', 'behaviors', 'calibration'];
      const [qualityText, ...fieldTexts] = await Promise.all([
        callModel(buildReviewPrompt(promptData, 'quality')),
        ...fields.map(f => callModel(buildReviewPrompt(promptData, f))),
      ]);
      const next: ParsedOutput = { quality: parseOutput(qualityText).quality, deliverables: '', leadership: '', behaviors: '', calibration: '' };
      fields.forEach((f, i) => {
        const key = FIELD_KEY[f];
        next[key] = afterMarker(fieldTexts[i], FIELD_MARKERS[key]);
      });
      setParsed(next);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
    setLoading(false);
  };

  const regenerateField = async (field: Field) => {
    setEditedFields(prev => { const s = new Set(prev); s.delete(field); return s; });
    setLoadingField(field);
    try {
      const res = await authenticatedFetch(`${PROXY_URL}/api/people/manager-review-v2/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: buildReviewPrompt(promptData, field) }),
      });
      const data = await res.json() as { text?: string; error?: string };
      if (!res.ok) { setError(data.error || `Request failed with status ${res.status}`); }
      else {
        const text = data.text || '';
        const key = FIELD_KEY[field];
        const marker = FIELD_MARKERS[key];
        const i = text.indexOf(marker);
        const content = i !== -1 ? text.slice(i + marker.length).trim() : text.trim();
        setParsed(p => p ? { ...p, [key]: content } : p);
      }
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
    setLoadingField(null);
  };

  const refineField = async (
    field: Field,
    currentText: string,
    instruction: string,
  ): Promise<{ ok: true } | { ok: false; message: string }> => {
    setRefiningField(field);
    try {
      const mode = field === 'delivery' ? 'refine-delivery' : field === 'leadership' ? 'refine-leadership' : field === 'calibration' ? 'refine-calibration' : 'refine-behaviors';
      const res = await authenticatedFetch(`${PROXY_URL}/api/people/manager-review-v2/generate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: buildReviewPrompt(promptData, mode, { currentText, instruction }) }),
      });
      const data = await res.json() as { text?: string; error?: string };
      if (!res.ok) { setRefiningField(null); return { ok: false, message: data.error || `Request failed with status ${res.status}` }; }
      const text = (data.text || '').trim();
      if (text.startsWith('RULES_VIOLATION:')) { setRefiningField(null); return { ok: false, message: text.replace(/^RULES_VIOLATION:\s*/i, '') }; }
      setParsed(p => p ? { ...p, [FIELD_KEY[field]]: text } : p);
      markEdited(field);
      setRefiningField(null);
      return { ok: true };
    } catch (e) {
      setRefiningField(null);
      return { ok: false, message: e instanceof Error ? e.message : 'Refinement failed' };
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      {/* Header */}
      <div style={{ background: C.card, borderRadius: 10, padding: 24, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: `1px solid ${C.border}` }}>
        <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>🤖 AI Review Generator</div>
        <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.6, margin: '0 0 16px' }}>
          Analyses your inputs, flags gaps and inconsistencies, then generates the ready-to-copy Culture Amp fields.
          Everything is grounded in the talent framework, rating scale, and your notes — nothing is invented.
        </p>
        {!confirmRegenerate ? (
          <button
            onClick={() => {
              if (editedFields.size > 0) { setConfirmRegenerate(true); }
              else { void generate(); }
            }}
            disabled={loading}
            style={{
              fontFamily: 'Inter', fontWeight: 700, fontSize: 14, border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer', borderRadius: 8,
              padding: '10px 22px', display: 'inline-flex', alignItems: 'center', gap: 8,
              opacity: loading ? 0.6 : 1, background: C.blue, color: '#fff',
            }}
          >
            {loading ? (
              <>
                <span style={{ width: 14, height: 14, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)', borderTopColor: '#fff', animation: 'spin 0.8s linear infinite', display: 'inline-block' }} />
                Generating…
              </>
            ) : parsed ? '↺ Regenerate AI Review' : '✨ Generate AI Review'}
          </button>
        ) : (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <span style={{ fontSize: 13, color: '#92400e' }}>
              ⚠️ Your manual edits to{' '}
              <strong>
                {(['delivery', 'leadership', 'behaviors', 'calibration'] as const).filter(f => editedFields.has(f)).map(f => FIELD_LABEL[f]).join(' and ')}
              </strong>
              {' '}will be lost.
            </span>
            <button
              onClick={() => { void generate(); }}
              style={{
                fontFamily: 'Inter', fontWeight: 700, fontSize: 13, border: 'none',
                cursor: 'pointer', borderRadius: 7, padding: '7px 16px',
                background: C.blue, color: '#fff',
              }}
            >Regenerate anyway</button>
            <button
              onClick={() => setConfirmRegenerate(false)}
              style={{
                fontFamily: 'Inter', fontWeight: 600, fontSize: 13,
                cursor: 'pointer', borderRadius: 7, padding: '7px 14px',
                background: 'transparent', border: `1px solid ${C.border}`, color: C.muted,
              }}
            >Cancel</button>
          </div>
        )}
        {error && (
          <div style={{ marginTop: 14, padding: '10px 14px', background: 'rgba(232,41,74,0.06)', border: '1px solid rgba(232,41,74,0.3)', borderRadius: 8, fontSize: 13, color: '#c0392b' }}>
            ⚠️ {error}
          </div>
        )}
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ background: C.card, borderRadius: 10, padding: 24, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: `1px solid ${C.border}` }}>
          {['Scoring review completeness…', 'Checking for gaps and inconsistencies…', 'Scanning for potential bias…', 'Drafting Culture Amp fields…'].map((msg, i, arr) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: i < arr.length - 1 ? `1px solid ${C.border}` : 'none' }}>
              <div style={{ width: 16, height: 16, borderRadius: '50%', flexShrink: 0, border: `2px solid ${C.blue}`, borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
              <span style={{ fontSize: 13, color: C.muted }}>{msg}</span>
            </div>
          ))}
        </div>
      )}

      {/* Results */}
      {parsed && !loading && (
        <>
          <div style={{ background: C.card, borderRadius: 10, padding: 24, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: `1px solid ${C.border}` }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: C.text, marginBottom: 16 }}>📊 Review Quality</div>
            <QualityDisplay
              raw={parsed.quality}
              hasInsufficientData={
                parsed.deliverables.includes('[INSUFFICIENT DATA') ||
                parsed.behaviors.includes('[INSUFFICIENT DATA') ||
                (hasLeadership && (parsed.leadership ?? '').includes('[INSUFFICIENT DATA'))
              }
              onNavigate={onNavigate}
              onOpenFeedback={onOpenFeedback}
            />
          </div>
          <div style={{ background: C.card, borderRadius: 10, padding: 24, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: `1px solid ${C.border}` }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 4, flexWrap: 'wrap' }}>
              <div style={{ fontWeight: 700, fontSize: 16, color: C.text }}>📋 Culture Amp — Ready to Copy</div>
              <a
                href="https://finn.eu.cultureamp.com/app/home"
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 5,
                  fontSize: 12, fontWeight: 600, color: C.blue,
                  border: `1px solid ${C.blue}`, borderRadius: 6,
                  padding: '5px 12px', textDecoration: 'none',
                  background: C.blue + '0d', whiteSpace: 'nowrap',
                  fontFamily: 'Inter',
                }}
              >Open Culture Amp ↗</a>
            </div>
            <div style={{ fontSize: 12, color: C.muted, marginBottom: 20, lineHeight: 1.5 }}>
              Edit the text fields as needed, then copy each one manually into Culture Amp.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <CARatingField num={1} label="Overall employee performance" rating={overallRating} />
              <CARatingField num={2} label="Delivery Rating" rating={deliveryRating} />
              <CATextField
                num={3} label="Delivery Feedback"
                hint="Please provide an overview of the employee's key Deliverables over the past 6 months, noting for each the impact achieved, strengths demonstrated, and opportunities for improvement."
                value={parsed.deliverables}
                onEdit={v => { markEdited('delivery'); setParsed(p => p ? { ...p, deliverables: v } : p); }}
                onRegenerate={() => { void regenerateField('delivery'); }}
                regenerating={loadingField === 'delivery'}
                onRefine={(instruction) => refineField('delivery', parsed?.deliverables ?? '', instruction)}
                refining={refiningField === 'delivery'}
              />
              {/* Numbers follow the Nov 2026 Culture Amp form. */}
              <CAChoiceField
                num={4}
                label="Does the employee perform in line with all three Leadership Values consistently?"
                answer={leadershipAnswer?.answer ?? null}
                detail={leadershipAnswer?.notMet.length ? `Not consistently: ${leadershipAnswer.notMet.join(', ')}` : undefined}
              />
              {hasLeadership && (
                <CATextField
                  num={4} label="Leadership Values Feedback"
                  hint="Please provide examples of situations where the employee demonstrated our Leadership Values in line with their Talent Framework, and where could they have been stronger?"
                  value={parsed.leadership ?? ''}
                  onEdit={v => { markEdited('leadership'); setParsed(p => p ? { ...p, leadership: v } : p); }}
                  onRegenerate={() => { void regenerateField('leadership'); }}
                  regenerating={loadingField === 'leadership'}
                  onRefine={(instruction) => refineField('leadership', parsed?.leadership ?? '', instruction)}
                  refining={refiningField === 'leadership'}
                />
              )}
              <CARatingField num={5} label="Behavior Rating" rating={behaviorsRating} />
              {FINN_VALUES.map((v, i) => (
                <CARatingField key={v.name} num={6 + i} label={v.name} rating={behaviorsData[v.name]?.rating ?? null} notShared />
              ))}
              <CATextField
                num={11} label="Behaviors Feedback"
                hint="Please provide examples of situations where the employee demonstrated our expected Behaviors in line with their Talent Framework, and where could they have been stronger?"
                value={parsed.behaviors}
                onEdit={v => { markEdited('behaviors'); setParsed(p => p ? { ...p, behaviors: v } : p); }}
                onRegenerate={() => { void regenerateField('behaviors'); }}
                regenerating={loadingField === 'behaviors'}
                onRefine={(instruction) => refineField('behaviors', parsed?.behaviors ?? '', instruction)}
                refining={refiningField === 'behaviors'}
              />
              <CAPromotionField num={12} promotion={promotion} />
              <CATextField
                num={13} label="Calibration summary (not visible to the employee)"
                hint="Based on your answers above, summarize the employee's strengths and development areas across Delivery and Behavior. Strengths: 1–3 bullet points. Development areas: 1–3 bullet points."
                value={parsed.calibration ?? ''}
                onEdit={v => { markEdited('calibration'); setParsed(p => p ? { ...p, calibration: v } : p); }}
                onRegenerate={() => { void regenerateField('calibration'); }}
                regenerating={loadingField === 'calibration'}
                onRefine={(instruction) => refineField('calibration', parsed?.calibration ?? '', instruction)}
                refining={refiningField === 'calibration'}
              />
            </div>
          </div>
        </>
      )}

      {/* Empty state */}
      {!parsed && !loading && !error && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[{ icon: '📊', label: 'Review Quality' }, { icon: '📋', label: 'Culture Amp — Ready to Submit' }].map(s => (
            <div key={s.label} style={{ background: C.card, borderRadius: 10, padding: 24, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', border: `1px solid ${C.border}`, opacity: 0.4 }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 12, color: C.muted }}>{s.icon} {s.label}</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {[95, 75, 50].map((w, i) => (
                  <div key={i} style={{ height: 11, background: C.border, borderRadius: 4, width: `${w}%` }} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
